import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { 
  MessageSquare, 
  Settings, 
  TestTube, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  RotateCcw, 
  Eye, 
  EyeOff, 
  Copy, 
  ExternalLink, 
  TrendingUp, 
  TrendingDown, 
  Star, 
  Crown, 
  Shield, 
  Timer, 
  Brain, 
  Route, 
  Lightning, 
  Zap, 
  Target, 
  Activity, 
  FileText, 
  Upload, 
  Download, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle2, 
  XCircle, 
  Info, 
  HelpCircle, 
  Bell, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Clock, 
  User, 
  Users, 
  Globe, 
  Database, 
  Server, 
  HardDrive, 
  Cpu, 
  Network, 
  Key, 
  Lock, 
  Unlock, 
  File, 
  FileImage, 
  FileVideo, 
  FileAudio, 
  FileSpreadsheet, 
  FilePdf, 
  FileZip, 
  FileCheck, 
  FileX, 
  FilePlus, 
  FileMinus, 
  FileEdit, 
  FileSearch, 
  FileSlash, 
  FileWarning, 
  FileQuestion, 
  FileHeart, 
  FileClock, 
  FileUp, 
  FileDown, 
  FileLeft, 
  FileRight, 
  FileBarChart, 
  FilePieChart, 
  FileLineChart, 
  FileScissors, 
  FileType, 
  FileSymlink, 
  FileStack, 
  FileTree, 
  FileBinary, 
  FileCog, 
  FileSettings, 
  FileShield, 
  FileLock, 
  FileUnlock, 
  FileKey, 
  FileSignature, 
  FileVerified, 
  FileBadge, 
  FileTag, 
  FileLabel, 
  FileBookmark, 
  FileStar, 
  FileFlag, 
  FileAlert, 
  FileInfo, 
  FileHelp, 
  FileMessage, 
  FileComment, 
  FileChat, 
  FileMail, 
  FilePhone, 
  FileVideoIcon, 
  FileAudioIcon, 
  FileImageIcon, 
  FileSpreadsheetIcon, 
  FilePdfIcon, 
  FileZipIcon, 
  FileCheckIcon, 
  FileXIcon, 
  FilePlusIcon, 
  FileMinusIcon, 
  FileEditIcon, 
  FileSearchIcon, 
  FileSlashIcon, 
  FileWarningIcon, 
  FileQuestionIcon, 
  FileHeartIcon, 
  FileClockIcon, 
  FileUpIcon, 
  FileDownIcon, 
  FileLeftIcon, 
  FileRightIcon, 
  FileBarChartIcon, 
  FilePieChartIcon, 
  FileLineChartIcon, 
  FileScissorsIcon, 
  FileTypeIcon, 
  FileSymlinkIcon, 
  FileStackIcon, 
  FileTreeIcon, 
  FileBinaryIcon, 
  FileCogIcon, 
  FileSettingsIcon, 
  FileShieldIcon, 
  FileLockIcon, 
  FileUnlockIcon, 
  FileKeyIcon, 
  FileCertificateIcon, 
  FileSignatureIcon, 
  FileVerifiedIcon, 
  FileBadgeIcon, 
  FileTagIcon, 
  FileLabelIcon, 
  FileBookmarkIcon, 
  FileStarIcon, 
  FileFlagIcon, 
  FileAlertIcon, 
  FileInfoIcon, 
  FileHelpIcon, 
  FileMessageIcon, 
  FileCommentIcon, 
  FileChatIcon, 
  FileMailIcon, 
  FilePhoneIcon
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const SMSConfiguration = () => {
  const [smsConfig, setSmsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('providers');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingRule, setEditingRule] = useState(null);

  useEffect(() => {
    loadSMSData();
  }, []);

  const loadSMSData = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getSMSConfiguration();
      if (response.success) {
        setSmsConfig(response.data);
      } else {
        setError('Failed to load SMS configuration');
      }
    } catch (err) {
      setError('Error loading SMS configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestProvider = async (providerId) => {
    try {
      const response = await MockDataService.testSMSProvider(providerId);
      if (response.success) {
        // Show success message
        alert('Provider test completed successfully');
        loadSMSData(); // Refresh data
      } else {
        alert('Provider test failed');
      }
    } catch (err) {
      alert('Error testing provider');
    }
  };

  const handleSaveProvider = async (providerData) => {
    try {
      const response = await MockDataService.updateSMSProvider(editingProvider.id, providerData);
      if (response.success) {
        setEditingProvider(null);
        loadSMSData();
      } else {
        alert('Failed to save provider');
      }
    } catch (err) {
      alert('Error saving provider');
    }
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      let response;
      if (editingTemplate.id) {
        response = await MockDataService.updateSMSTemplate(editingTemplate.id, templateData);
      } else {
        response = await MockDataService.createSMSTemplate(templateData);
      }
      
      if (response.success) {
        setEditingTemplate(null);
        loadSMSData();
      } else {
        alert('Failed to save template');
      }
    } catch (err) {
      alert('Error saving template');
    }
  };

  const handleSaveRule = async (ruleData) => {
    try {
      let response;
      if (editingRule.id) {
        response = await MockDataService.updateSMSAutomationRule(editingRule.id, ruleData);
      } else {
        response = await MockDataService.createSMSAutomationRule(ruleData);
      }
      
      if (response.success) {
        setEditingRule(null);
        loadSMSData();
      } else {
        alert('Failed to save rule');
      }
    } catch (err) {
      alert('Error saving rule');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'testing': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTestStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'pending': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!smsConfig) {
    return (
      <Alert className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>No SMS configuration data available</AlertDescription>
      </Alert>
    );
  }

  // Safely extract data with defaults
  const providers = smsConfig.providers || [];
  const templates = smsConfig.templates || [];
  const automationRules = smsConfig.automation?.rules || [];
  const automationSchedules = smsConfig.automation?.schedules || [];
  const analyticsTrends = smsConfig.analytics?.trends || [];
  const monitoringAlerts = smsConfig.monitoring?.alerts || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SMS Configuration</h1>
          <p className="text-gray-600">Manage SMS providers, templates, and automation rules</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={loadSMSData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'providers', label: 'Providers', icon: MessageSquare },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'automation', label: 'Automation', icon: Zap },
            { id: 'analytics', label: 'Analytics', icon: Activity },
            { id: 'compliance', label: 'Compliance', icon: Shield },
            { id: 'monitoring', label: 'Monitoring', icon: Target }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-[#006600] text-[#006600]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Providers Tab */}
      {activeTab === 'providers' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">SMS Providers</h2>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Provider
            </Button>
          </div>

          <div className="grid gap-6">
            {providers.length > 0 ? providers.map((provider) => (
              <Card key={provider.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="h-6 w-6 text-blue-600" />
                      <div>
                        <CardTitle className="text-lg">{provider.name}</CardTitle>
                        <p className="text-sm text-gray-600 capitalize">{provider.type} Provider</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(provider.status)}>
                        {provider.status}
                      </Badge>
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestProvider(provider.id)}
                        >
                          <TestTube className="h-4 w-4 mr-1" />
                          Test
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingProvider(provider)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Configuration</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Phone Number:</span>
                          <span className="font-medium">{provider.configuration.phoneNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Region:</span>
                          <span className="font-medium">{provider.configuration.region}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Last Tested:</span>
                          <span className="font-medium">
                            {new Date(provider.lastTested).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Test Status:</span>
                          <span className={`font-medium ${getTestStatusColor(provider.testStatus)}`}>
                            {provider.testStatus}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Features & Pricing</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Features:</span>
                          <span className="font-medium">{(provider.features && provider.features.length > 0) ? provider.features.join(', ') : 'None'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">SMS Cost:</span>
                          <span className="font-medium">${provider.pricing.smsPerMessage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">MMS Cost:</span>
                          <span className="font-medium">${provider.pricing.mmsPerMessage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">International:</span>
                          <span className="font-medium">${provider.pricing.internationalPerMessage}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <p>No providers configured</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">SMS Templates</h2>
            <Button onClick={() => setEditingTemplate({})}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>

          <div className="grid gap-4">
            {templates.length > 0 ? templates.map((template) => (
              <Card key={template.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        <p className="text-sm text-gray-600 capitalize">{template.type ? template.type.replace('_', ' ') : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(template.status)}>
                        {template.status}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingTemplate(template)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Content</Label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                        {template.content}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Variables</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {template.variables && template.variables.length > 0 ? template.variables.map((variable) => (
                          <Badge key={variable} variant="outline" className="text-xs">
                            {`{${variable}}`}
                          </Badge>
                        )) : (
                          <span className="text-xs text-gray-500">No variables</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Created: {new Date(template.createdAt).toLocaleString()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <p>No templates configured</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Automation Tab */}
      {activeTab === 'automation' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Automation Rules</h2>
            <Button onClick={() => setEditingRule({})}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          </div>

          <div className="grid gap-4">
            {automationRules.length > 0 ? automationRules.map((rule) => (
              <Card key={rule.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Zap className="h-5 w-5 text-blue-600" />
                      <div>
                        <CardTitle className="text-base">{rule.name}</CardTitle>
                        <p className="text-sm text-gray-600">Trigger: {rule.trigger ? rule.trigger.replace('_', ' ') : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(rule.status)}>
                        {rule.status}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingRule(rule)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Conditions</h4>
                      <div className="space-y-1 text-sm">
                        {rule.conditions && Object.keys(rule.conditions).length > 0 ? Object.entries(rule.conditions).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600 capitalize">{key ? key.replace('_', ' ') : 'N/A'}:</span>
                            <span className="font-medium">
                              {Array.isArray(value) ? value.join(', ') : value || 'N/A'}
                            </span>
                          </div>
                        )) : (
                          <div className="text-sm text-gray-500">No conditions</div>
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Actions</h4>
                      <div className="space-y-1 text-sm">
                        {rule.actions && Object.keys(rule.actions).length > 0 ? Object.entries(rule.actions).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600 capitalize">{key ? key.replace('_', ' ') : 'N/A'}:</span>
                            <span className="font-medium">{value || 'N/A'}</span>
                          </div>
                        )) : (
                          <div className="text-sm text-gray-500">No actions</div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <p>No automation rules configured</p>
              </div>
            )}
          </div>

          {/* Schedules */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Sending Schedules</h3>
            <div className="grid gap-4">
              {automationSchedules.length > 0 ? automationSchedules.map((schedule) => (
                <Card key={schedule.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Clock className="h-5 w-5 text-blue-600" />
                        <div>
                          <CardTitle className="text-base">{schedule.name}</CardTitle>
                          <p className="text-sm text-gray-600">{schedule.description}</p>
                        </div>
                      </div>
                      <Badge className={getStatusColor(schedule.status || 'inactive')}>
                        {schedule.status || 'N/A'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Timezone:</span>
                        <span className="font-medium">{schedule.timezone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Business Hours:</span>
                        <span className="font-medium">
                          {schedule.businessHours?.start && schedule.businessHours?.end 
                            ? `${schedule.businessHours.start} - ${schedule.businessHours.end}`
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Days:</span>
                        <span className="font-medium">
                          {schedule.businessHours?.days && schedule.businessHours.days.length > 0 ? schedule.businessHours.days.join(', ') : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No schedules configured</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">SMS Analytics</h2>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <MessageSquare className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Messages</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {(smsConfig.analytics?.smsMetrics?.totalMessages || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {smsConfig.analytics?.performanceMetrics?.deliveryRate || 0}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Timer className="h-8 w-8 text-yellow-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {smsConfig.analytics?.performanceMetrics?.averageResponseTime || 0}m
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Star className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Satisfaction</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {smsConfig.analytics?.performanceMetrics?.customerSatisfaction || 0}/5
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Message Volume */}
          <Card>
            <CardHeader>
              <CardTitle>Message Volume</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {smsConfig.analytics?.smsMetrics?.messageVolume?.today || 0}
                  </p>
                  <p className="text-sm text-gray-600">Today</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {smsConfig.analytics?.smsMetrics?.messageVolume?.thisWeek || 0}
                  </p>
                  <p className="text-sm text-gray-600">This Week</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {smsConfig.analytics?.smsMetrics?.messageVolume?.thisMonth || 0}
                  </p>
                  <p className="text-sm text-gray-600">This Month</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    ${smsConfig.analytics?.performanceMetrics?.costPerMessage || 0}
                  </p>
                  <p className="text-sm text-gray-600">Cost per Message</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trends */}
          <Card>
            <CardHeader>
              <CardTitle>Message Trends (Last 20 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analyticsTrends.length > 0 ? analyticsTrends.slice(-10).map((trend, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">
                      {new Date(trend.date).toLocaleDateString()}
                    </span>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-blue-600">Sent: {trend.sent}</span>
                      <span className="text-green-600">Delivered: {trend.delivered}</span>
                      <span className="text-red-600">Failed: {trend.failed}</span>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-4 text-gray-500">
                    <p>No trend data available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">Compliance Settings</h2>

          {/* Opt-out Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Opt-out Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Opt-out Management</span>
                  <Badge className={smsConfig.compliance?.optOutManagement?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {smsConfig.compliance?.optOutManagement?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-700">Opt-out Keywords</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {smsConfig.compliance?.optOutManagement?.optOutKeywords && smsConfig.compliance.optOutManagement.optOutKeywords.length > 0 ? smsConfig.compliance.optOutManagement.optOutKeywords.map((keyword) => (
                      <Badge key={keyword} variant="outline" className="text-xs">
                        {keyword}
                      </Badge>
                    )) : (
                      <span className="text-xs text-gray-500">No keywords configured</span>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Opt-in Keywords</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {smsConfig.compliance?.optOutManagement?.optInKeywords && smsConfig.compliance.optOutManagement.optInKeywords.length > 0 ? smsConfig.compliance.optOutManagement.optInKeywords.map((keyword) => (
                      <Badge key={keyword} variant="outline" className="text-xs">
                        {keyword}
                      </Badge>
                    )) : (
                      <span className="text-xs text-gray-500">No keywords configured</span>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Opt-out Message</Label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {smsConfig.compliance?.optOutManagement?.optOutMessage || 'N/A'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Opt-in Message</Label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {smsConfig.compliance?.optOutManagement?.optInMessage || 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Database className="h-5 w-5 mr-2" />
                Data Retention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Message Retention</Label>
                    <p className="text-sm text-gray-900">{smsConfig.compliance?.dataRetention?.messageRetention || 0} days</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Opt-out Retention</Label>
                    <p className="text-sm text-gray-900">{smsConfig.compliance?.dataRetention?.optOutRetention || 0} days</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Analytics Retention</Label>
                    <p className="text-sm text-gray-900">{smsConfig.compliance?.dataRetention?.analyticsRetention || 0} days</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Auto Delete</Label>
                    <Badge className={smsConfig.compliance?.dataRetention?.autoDelete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {smsConfig.compliance?.dataRetention?.autoDelete ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Regulations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Regulatory Compliance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">TCPA Compliant</span>
                    <Badge className={smsConfig.compliance?.regulations?.tcpaCompliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {smsConfig.compliance?.regulations?.tcpaCompliant ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">GDPR Compliant</span>
                    <Badge className={smsConfig.compliance?.regulations?.gdprCompliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {smsConfig.compliance?.regulations?.gdprCompliant ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Canada Anti-Spam</span>
                    <Badge className={smsConfig.compliance?.regulations?.canadaAntiSpamCompliant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {smsConfig.compliance?.regulations?.canadaAntiSpamCompliant ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Consent Required</span>
                    <Badge className={smsConfig.compliance?.regulations?.consentRequired ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {smsConfig.compliance?.regulations?.consentRequired ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Double Opt-in</span>
                    <Badge className={smsConfig.compliance?.regulations?.doubleOptIn ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {smsConfig.compliance?.regulations?.doubleOptIn ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monitoring Tab */}
      {activeTab === 'monitoring' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">System Monitoring</h2>

          {/* System Health */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="h-5 w-5 mr-2" />
                System Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600 capitalize">
                    {smsConfig.monitoring?.systemHealth?.status || 'unknown'}
                  </p>
                  <p className="text-sm text-gray-600">Status</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {smsConfig.monitoring?.systemHealth?.uptime || 0}%
                  </p>
                  <p className="text-sm text-gray-600">Uptime</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {smsConfig.monitoring?.messageQuality?.successRate || 0}%
                  </p>
                  <p className="text-sm text-gray-600">Success Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Message Quality */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Target className="h-5 w-5 mr-2" />
                Message Quality Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-xl font-bold text-blue-600">
                    {smsConfig.monitoring?.messageQuality?.averageLatency || 0}s
                  </p>
                  <p className="text-sm text-gray-600">Avg Latency</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-green-600">
                    {smsConfig.monitoring?.messageQuality?.successRate || 0}%
                  </p>
                  <p className="text-sm text-gray-600">Success Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-red-600">
                    {smsConfig.monitoring?.messageQuality?.errorRate || 0}%
                  </p>
                  <p className="text-sm text-gray-600">Error Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-yellow-600">
                    {smsConfig.monitoring?.messageQuality?.retryRate || 0}%
                  </p>
                  <p className="text-sm text-gray-600">Retry Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="h-5 w-5 mr-2" />
                Active Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {monitoringAlerts.length > 0 ? monitoringAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {alert.type === 'warning' && <AlertCircle className="h-5 w-5 text-yellow-600" />}
                      {alert.type === 'info' && <Info className="h-5 w-5 text-blue-600" />}
                      {alert.type === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                      <div>
                        <p className="font-medium text-gray-900">{alert.title}</p>
                        <p className="text-sm text-gray-600">{alert.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(alert.status)}>
                        {alert.status}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(alert.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-4 text-gray-500">
                    <p>No alerts</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Integration Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Globe className="h-5 w-5 mr-2" />
                Integration Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Ticketing Integration</span>
                  <Badge className={smsConfig.integration?.ticketingIntegration?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {smsConfig.integration?.ticketingIntegration?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">CRM Integration</span>
                  <Badge className={smsConfig.integration?.crmIntegration?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {smsConfig.integration?.crmIntegration?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Webhook Integration</span>
                  <Badge className={smsConfig.integration?.webhookIntegration?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {smsConfig.integration?.webhookIntegration?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SMSConfiguration;
